import React, { useCallback, useEffect, useRef, useState } from "react";

import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import useDefaultCustomDomain from "@/features/react-editor/hooks/useDefaultCustomDomain";
import shouldShowV2Debuggers from "@/features/react-editor/utils/shouldShowV2Debuggers";
import { useCurrentFile } from "@/features/react-editor/utils/useCurrentFile";
import InlineAlert from "@replo/design-system/components/alert/InlineAlert";
import { toast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import Popover from "@replo/design-system/components/popover/Popover";
import { Progress } from "@replo/design-system/components/shadcn/core/progress";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";
import { ArrowUpRight, CheckCircle2, Globe, X } from "lucide-react";
import { PuffLoader } from "react-spinners";
import { z } from "zod";

import { invalidateResourcesRelatedToDeployment } from "./invalidateResourcesRelatedToDeployment";

export const PublishButton = ({
  routeType,
  fileIdsToPublish,
  publishedSiteUrl,
  isViewableLive,
}: {
  routeType: "funnel" | "page";
  fileIdsToPublish: string[];
  publishedSiteUrl?: string;
  isViewableLive: boolean;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showTemporaryState, setShowTemporaryState] = useState<
    | { type: "success" }
    | {
        type: "error";
        details?: {
          type: "build" | "deploy";
          stdout: string;
          stderr: string;
        };
      }
    | null
  >(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { project } = useCurrentProjectContext();

  const defaultCustomDomain = useDefaultCustomDomain();
  const showAddDomainButton = !Boolean(defaultCustomDomain);

  const currentFile = useCurrentFile();

  const {
    mutate: deploy,
    isPending,
    isSuccess,
  } = trpc.v2ShopDeployment.deploy.useMutation({
    onMutate: async ({ projectId, fileIdsToPublish }) => {
      await trpcUtils.v2ShopDeployment.findDeployments.cancel({
        projectId,
        deployStatus: "PENDING",
      });

      const previousPendingDeployments =
        trpcUtils.v2ShopDeployment.findDeployments.getData({
          projectId,
          deployStatus: "PENDING",
        });

      const filesPublished = fileIdsToPublish.map((fileId) => ({
        id: fileId,
        createdAt: new Date(),
        updatedAt: new Date(),
        deletedAt: null,
        name: "Publishing...",
        fileType: "page",
        path: null,
        projectId,
        pageRoute: null,
        pageMetadataTitle: null,
        pageMetadataDescription: null,
        pageMetadataOpenGraphImageUrl: null,
        content: "",
        lastPublishedFileVersionId: null,
      }));

      const optimisticDeployment = {
        id: `optimistic-${Date.now()}`,
        projectId,
        deployStatus: "PENDING" as const,
        filesPublished,
      };

      trpcUtils.v2ShopDeployment.findDeployments.setData(
        { projectId, deployStatus: "PENDING" },
        (oldData) => {
          if (!oldData) {
            return [optimisticDeployment];
          }
          return [optimisticDeployment, ...oldData];
        },
      );

      return { previousPendingDeployments };
    },
    onSettled: (_data, _error, { projectId }) => {
      void trpcUtils.v2ShopDeployment.findDeployments.invalidate({
        projectId,
        deployStatus: "PENDING",
      });
    },
    onSuccess: () => {
      toast({
        header: `${routeType === "funnel" ? "Funnel" : "Page"} Successfully Published`,
        message: `Your updated ${routeType === "funnel" ? "pages are" : "page is"} now live.`,
        cta: "View Live",
        ctaOnClick: () => {
          window.open(publishedSiteUrl, "_blank");
        },
      });

      invalidateResourcesRelatedToDeployment();
    },
    onError: (error, { projectId }, context) => {
      const details = z
        .object({
          type: z.enum(["build", "deploy"]),
          stdout: z.string(),
          stderr: z.string(),
        })
        .safeParse(error.shape?.data?.details)?.data;
      setShowTemporaryState({
        type: "error",
        details,
      });
      if (context?.previousPendingDeployments) {
        trpcUtils.v2ShopDeployment.findDeployments.setData(
          { projectId, deployStatus: "PENDING" },
          context.previousPendingDeployments,
        );
      }
    },
  });

  useEffect(() => {
    if (isSuccess && !isPending) {
      setShowTemporaryState({ type: "success" });
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isSuccess, isPending]);

  if (!project) {
    return null;
  }

  const enablePublishButton =
    Boolean(currentFile) &&
    currentFile?.latestFileVersion.id !==
      currentFile?.lastPublishedFileVersionId;

  const siteUrlWithoutProtocol = publishedSiteUrl?.replace("https://", "");

  const shortenedSiteUrl =
    siteUrlWithoutProtocol?.slice(0, 28) +
    (siteUrlWithoutProtocol?.length && siteUrlWithoutProtocol?.length > 27
      ? "..."
      : "");

  const handlePublish = () => {
    setShowTemporaryState(null);

    deploy({
      projectId: project.id,
      fileIdsToPublish,
      fileIdsToUnpublish: [],
    });
  };

  const handlePopoverOpenChange = (open: boolean) => {
    if (isPending) {
      return;
    }

    if (!open && showTemporaryState) {
      setShowTemporaryState(null);
    }

    setIsPopoverOpen(open);
  };

  const renderPopoverContent = () => {
    if (isPending || showTemporaryState) {
      let title: string;
      let description: string;

      if (showTemporaryState?.type === "success") {
        title = `${routeType === "funnel" ? "Funnel" : "Page"} Published Successfully`;
        description = `Your ${
          fileIdsToPublish.length > 1 ? "pages are" : "page is"
        } now live.`;
      } else if (showTemporaryState?.type === "error") {
        title = `Publishing Failed`;
        description = "Please try again or contact support@replo.app.";
      } else {
        title = "Publishing your page...";
        description = "Feel free to leave this tab.";
      }

      return (
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={8}
          shouldPreventDefaultOnInteractOutside={
            showTemporaryState?.type !== "success"
          }
          title={title}
          description={description}
          hideCloseButton={isPending}
          className="w-80"
        >
          <div className="flex flex-col gap-2">
            {shouldShowV2Debuggers() &&
              showTemporaryState?.type === "error" &&
              showTemporaryState.details && (
                <InlineAlert
                  variant="error"
                  layoutClassName="z-50"
                  endEnhancer={
                    <Popover.Root>
                      <Popover.Content
                        className="w-[500px]"
                        side="left"
                        shouldPreventDefaultOnInteractOutside={false}
                        hideCloseButton
                        align="start"
                      >
                        <div className="flex flex-col gap-2 w-full">
                          <Textarea
                            value={showTemporaryState.details.stdout}
                            layoutClassName="h-[200px] w-full"
                            onChange={() => {}}
                            UNSAFE_className="font-mono text-xs"
                          />
                          <Textarea
                            value={showTemporaryState.details.stderr}
                            layoutClassName="h-[200px] w-full"
                            onChange={() => {}}
                            UNSAFE_className="font-mono text-xs"
                          />
                        </div>
                      </Popover.Content>
                      <Popover.Trigger asChild>
                        <button className="bg-surface flex min-w-fit flex-row items-center gap-1 rounded-sm px-2 py-1 text-muted whitespace-nowrap text-sm">
                          View logs
                        </button>
                      </Popover.Trigger>
                    </Popover.Root>
                  }
                >
                  <span className="h-full flex justify-center items-center">
                    [Debug] Error publishing
                  </span>
                </InlineAlert>
              )}
            <PublishingLoadingContent
              status={showTemporaryState?.type ?? null}
            />
          </div>
        </Popover.Content>
      );
    }

    return (
      <Popover.Content
        side="bottom"
        align="end"
        sideOffset={8}
        shouldPreventDefaultOnInteractOutside={false}
        hideCloseButton={true}
        className="w-80"
        footer={
          <PublishPopoverFooter
            isPending={isPending}
            handlePublish={handlePublish}
            showAddDomainButton={showAddDomainButton}
          />
        }
        onOpenAutoFocus={(e: Event) => {
          e.preventDefault();
        }}
      >
        <PublishPopoverContent
          siteUrl={publishedSiteUrl}
          siteUrlWithoutProtocol={siteUrlWithoutProtocol}
          shortenedSiteUrl={shortenedSiteUrl}
          isViewableLive={isViewableLive}
        />
      </Popover.Content>
    );
  };

  return (
    <Popover.Root isOpen={isPopoverOpen} onOpenChange={handlePopoverOpenChange}>
      <Popover.Trigger asChild>
        <Button
          variant="primary"
          size="base"
          onClick={() => setIsPopoverOpen(true)}
          disabled={!enablePublishButton}
          tooltipText={
            !enablePublishButton
              ? "No changes to publish - your file is up to date!"
              : undefined
          }
        >
          {routeType === "funnel" ? "Publish Funnel" : "Publish Page"}
        </Button>
      </Popover.Trigger>

      {renderPopoverContent()}
    </Popover.Root>
  );
};

const PublishingLoadingContent = ({
  status,
}: {
  status: "success" | "error" | null;
}) => {
  const simulatedProgress = useSimulatedProgress(
    status === null, // isActive
    75000, // 75 seconds duration
    2, // 2 updates per second for smoother progress
  );

  // Use simulated progress when still loading, 100% when successful
  const displayProgress =
    status === "success" || status === "error" ? 100 : simulatedProgress;
  const displayPercentage = Math.round(displayProgress);

  return (
    <div className="flex flex-col items-center">
      {status === "success" && (
        <div className="relative w-72 h-32">
          <img
            src="/images/shops/successful-publish.png"
            alt="Successful publish"
            className="absolute top-1/2 left-1/2 w-64 -translate-x-[50%] -translate-y-[50%] z-10"
          />
        </div>
      )}
      {status === "error" && (
        <div className="relative w-64 h-32">
          <img
            src="/images/shops/failed-publish.png"
            alt="Failed publish"
            className="absolute top-1/2 left-1/2 w-80 -translate-x-[50%] -translate-y-[50%] z-10"
          />
        </div>
      )}
      {!status && (
        <div className="relative w-32 h-32">
          <img
            src="/images/shops/Globe.png"
            alt="Globe"
            className="absolute top-1/2 left-1/2 w-16 h-16 -translate-x-[56%] -translate-y-[56%] z-10"
          />
          <PuffLoader color="#2563EB" size={120} speedMultiplier={0.75} />
        </div>
      )}

      <div className="w-full flex gap-3 items-center">
        <Progress
          value={displayProgress}
          className="h-2 bg-gray-200 [&>div]:bg-black [&>div]:transition-all [&>div]:duration-500 [&>div]:ease-out" // eslint-disable-line replo/no-tailwind-literal-color
        />
        {!status && (
          <span className="text-header-h5 font-semibold text-base">
            {displayPercentage}%
          </span>
        )}
        {status === "success" && <CheckCircle2 className="w-6 h-6" />}
        {status === "error" && <X className="w-6 h-6" />}
      </div>
    </div>
  );
};

const PublishPopoverContent = ({
  siteUrl,
  siteUrlWithoutProtocol,
  shortenedSiteUrl,
  isViewableLive,
}: {
  siteUrl?: string;
  siteUrlWithoutProtocol?: string;
  shortenedSiteUrl: string;
  isViewableLive: boolean;
}) => {
  return (
    <div className="flex flex-col gap-4 p-3 border-border border-0.5 rounded-lg bg-sheet">
      {siteUrl && (
        <Button
          variant="link"
          size="base"
          onClick={
            isViewableLive
              ? () => {
                  window.open(siteUrl, "_blank");
                }
              : undefined
          }
          startEnhancer={<Globe className="w-4 h-4 mr-1.5" />}
          tooltipText={siteUrlWithoutProtocol}
          UNSAFE_contentClassName={
            isViewableLive ? "text-default" : "text-muted"
          }
          disabled={!isViewableLive}
          titleAlignment="start"
        >
          <div className="flex items-center">
            {shortenedSiteUrl}
            {isViewableLive && <ArrowUpRight className="w-4 h-4 ml-1.5" />}
          </div>
        </Button>
      )}
    </div>
  );
};

const PublishPopoverFooter = ({
  isPending,
  handlePublish,
  showAddDomainButton,
}: {
  isPending: boolean;
  handlePublish: () => void;
  showAddDomainButton: boolean;
}) => {
  const { project } = useCurrentProjectContext();

  const handleAddDomain = () => {
    if (project?.id) {
      // NOTE (Gabe 2025-07-24): We don't useNavigate here because we explicitly
      // want this to be a server loaded navigation so that Cross-Origin headers
      // are not used (they break Entri's oauth flow). Not a big deal either
      // way, using a direct nav here makes for a slightly more seamless UX. See
      // more in App.tsx's shouldTriggerServerLoadedNavigation.
      window.location.href = `/shops/${project.id}/settings/shop/general`;
    }
  };

  return (
    <div className="flex gap-3">
      {showAddDomainButton && (
        <Button
          variant="secondary"
          size="base"
          onClick={handleAddDomain}
          layoutClassName="flex-1"
        >
          Add Domain
        </Button>
      )}
      <Button
        variant="primary"
        size="base"
        onClick={handlePublish}
        isLoading={isPending}
        layoutClassName="flex-1"
      >
        Publish
      </Button>
    </div>
  );
};

// Simple easing function - starts fast, slows down at the end
const easeOut = (t: number): number => {
  return 1 - Math.pow(1 - t, 2);
};

const useSimulatedProgress = (
  isActive: boolean,
  targetDuration: number = 75000, // 75 seconds
  updatesPerSecond: number = 4, // How often to update (1 = once per second, 2 = twice per second, 0.5 = every 2 seconds)
) => {
  const [progress, setProgress] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousProgressRef = useRef(0);

  // Convert updates per second to milliseconds
  const updateInterval = 1000 / updatesPerSecond;

  const updateProgress = useCallback(() => {
    if (!startTimeRef.current || !isActive) {
      return;
    }

    const elapsed = Date.now() - startTimeRef.current;
    const normalizedTime = Math.min(elapsed / targetDuration, 1);

    // Simple progression - fast at first, slower at the end
    const easedProgress = easeOut(normalizedTime);

    // Add small random bump (0-2%) to make it feel less predictable
    const randomBump = Math.random() * 0.02;
    const calculatedProgress = Math.min((easedProgress + randomBump) * 98, 98); // Cap at 98%

    // Ensure progress never decreases
    const newProgress = Math.max(
      calculatedProgress,
      previousProgressRef.current,
    );

    previousProgressRef.current = newProgress;
    setProgress(newProgress);

    // Stop the interval when we reach the end
    if (normalizedTime >= 1 && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [isActive, targetDuration]);

  useEffect(() => {
    if (isActive && !startTimeRef.current) {
      startTimeRef.current = Date.now();
      previousProgressRef.current = 0;
      setProgress(0);

      // Start updates after a brief delay to show 0% first
      setTimeout(() => {
        if (isActive) {
          intervalRef.current = setInterval(updateProgress, updateInterval);
        }
      }, 100);
    } else if (!isActive) {
      startTimeRef.current = null;
      previousProgressRef.current = 0;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive, updateProgress, updateInterval]);

  return progress;
};
